import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Icon, Table } from 'semantic-ui-react'
import { milkSamplesIDs } from '~/Constants'
import { DownloadService } from '~/api/services/downloadFiles'
import MilkMoneyAccountingService from '~/api/services/milkMoneyAccounting'
import Layout from '~/components/Layout'
import i18n from '~/components/i18n'
import { RouteTypes } from '~/routes/constants'
import { formatNumber, formatNumberWithoutFractions, handleErrors } from '~/utils/helpers'

const getShowDataLabelsAndKeys = () => [
  { label: i18n.t('Supplier No.'), key: 'supplierNo' },
  { label: i18n.t('Company name'), key: 'companyName' },
  { label: i18n.t('Created'), key: 'createdAt', type: 'date', format: 'DD.MM.YYYY HH:mm' },
  { label: i18n.t('Procurement month'), key: 'date', type: 'date', format: 'MMM YYYY' },
  { label: i18n.t('Original file'), key: 'file', type: 'file' },
  { label: i18n.t('Imported via FTP?'), key: 'ftpImport', type: 'icon' },
  { label: i18n.t('Sampling time'), key: 'samplingDate', type: 'date', format: 'DD.MM.YYYY HH:mm' },
  { label: i18n.t('Fat (in %)'), key: 'fat', type: 'number', format: 'percent' },
  { label: i18n.t('Protein (in %)'), key: 'protein', type: 'number', format: 'percent' },
  { label: i18n.t('Lactose (in %)'), key: 'lactose', type: 'number', format: 'percent' },
  { label: i18n.t('Cell count (1000/ml)'), key: 'cellCount' },
  { label: i18n.t('Germ count (1000/ml)'), key: 'germCount' },
  { label: i18n.t('Inhibitor'), key: 'inhibitorLabel' },
  { label: i18n.t('Freezing point (in °C)'), key: 'freezingPoint', type: 'number', format: 'percent' },
  { label: i18n.t('Urea (mg/dl)'), key: 'urea', type: 'number', format: 'percent' },
  { label: i18n.t('Fat-free dry matter (in %)'), key: 'fatFreeDryMatter', type: 'number', format: 'percent' },
  { label: i18n.t('Free fatty acid (in %)'), key: 'freeFattyAcid', type: 'number', format: 'percent' }
];

const getFarmerColumns = () => [
  { label: i18n.t('Supplier No.'), key: 'supplierNo' },
  { label: i18n.t('Company name'), key: 'companyName' },
  { label: i18n.t('Created'), key: 'createdAt', type: 'date', format: 'DD.MM.YYYY HH:mm' },
  { label: i18n.t('Procurement month'), key: 'billingMonth', type: 'date', format: 'MMM YYYY' },
  { label: i18n.t('Original file'), key: 'file', type: 'file' },
  { label: i18n.t('Imported via FTP?'), key: 'ftpImport', type: 'icon' },
  { label: i18n.t('Class'), key: 'class', type: 'number' },
  { label: i18n.t('Pay class'), key: 'payClass' },
  { label: i18n.t('Fat (in %)'), key: 'fat', type: 'number', format: 'percent' },
  { label: i18n.t('Protein (in %)'), key: 'protein', type: 'number', format: 'percent' },
  { label: i18n.t('Lactose (in %)'), key: 'lactose', type: 'number', format: 'percent' },
  { label: i18n.t('Cell count (1000/ml)'), key: 'cellCount' },
  { label: i18n.t('Germ count (1000/ml)'), key: 'germCount' },
  { label: i18n.t('Inhibitor'), key: 'inhibitorLabel' },
  { label: i18n.t('Freezing point (in °C)'), key: 'freezingPoint', type: 'number', format: 'percent' },
  { label: i18n.t('Urea (mg/dl)'), key: 'urea' },
  { label: i18n.t('Fat-free dry matter (in %)'), key: 'fatFreeDryMatter', type: 'number', format: 'percent' },
  { label: i18n.t('Cell count deduction'), key: 'cellCountDeduction', type: 'deductionType' },
  { label: i18n.t('Germ deduction'), key: 'germDeduction', type: 'deductionType' }
]

const getPseudomColumns = () => [
  { label: i18n.t('Supplier No.'), key: 'supplierNo' },
  { label: i18n.t('Procurement month'), key: 'date', type: 'date', format: 'MMM YYYY' },
  { label: i18n.t('Date sample 1'), key: 'sample1Date', type: 'date', format: 'DD.MM.YYYY' },
  { label: i18n.t('File'), key: 'file', type: 'file' },
  { label: i18n.t('Imported via FTP?'), key: 'ftpImport', type: 'icon' },
  { label: i18n.t('Date sample 1'), key: 'sample1Date', type: 'date', format: 'DD.MM.YYYY' },
  { label: i18n.t('Value sample 1 (1.000/ml)'), key: 'sample1Value', type: 'no-unit-int-number' },
  { label: i18n.t('Date sample 2'), key: 'sample2Date', type: 'date', format: 'DD.MM.YYYY' },
  { label: i18n.t('Value sample 2 (1.000/ml)'), key: 'sample2Value', type: 'no-unit-int-number' },
  { label: i18n.t('Notice letter'), key: 'noticeLetter' },
  { label: i18n.t('Date sample 3'), key: 'sample3Date', type: 'date', format: 'DD.MM.YYYY' },
  { label: i18n.t('Value sample 3 (1.000/ml)'), key: 'sample3Value', type: 'no-unit-int-number' },
  { label: i18n.t('Control level'), key: 'levelControl', type: 'date', format: 'DD.MM.YYYY' },
  { label: i18n.t('Date sample 4'), key: 'sample4Date', type: 'date', format: 'DD.MM.YYYY' },
  { label: i18n.t('Value sample 4 (1.000/ml)'), key: 'sample4Value', type: 'no-unit-int-number' },
  { label: i18n.t('Date sample 5'), key: 'sample5Date', type: 'date', format: 'DD.MM.YYYY' },
  { label: i18n.t('Value sample 5 (1.000/ml)'), key: 'sample5Value', type: 'no-unit-int-number' },
  { label: i18n.t('Date sample 6'), key: 'sample6Date', type: 'date', format: 'DD.MM.YYYY' },
  { label: i18n.t('Value sample 6 (1.000/ml)'), key: 'sample6Value', type: 'no-unit-int-number' },
  { label: i18n.t('Germ count over 50,000ml'), key: 'germValue' },
  { label: i18n.t('Status level (current month)'), key: 'levelStatus', type: 'number' },
  { label: i18n.t('Status level (last month)'), key: 'levelStatusLastMonth', type: 'number' }
]

const dairyRemoveKeys = ['companyName', 'supplierNo', 'samplingDate', 'inhibitor', 'freeFattyAcid']

const ShowSamples: React.FC = () => {
  const { t } = useTranslation()
  const [showData, setShowData] = useState(null)
  const [isBusy, setIsBusy] = useState(false)
  const { sampleType, id } = useParams()

  const [
    showDataLabelsAndKeys,
    farmerColumns,
    pseudomColumns
  ] = [getShowDataLabelsAndKeys(), getFarmerColumns(), getPseudomColumns()]

  useEffect(() => {
    fetchShowData()
  }, [])

  async function fetchShowData() {
    setIsBusy(true)
    try {
      const response = await MilkMoneyAccountingService.getMilkSamplesShowData(sampleType, id)
      setShowData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function getTitle() {
    switch (sampleType) {
      case milkSamplesIDs.allIndividual:
        return t('All individual values')
      case milkSamplesIDs.farmer:
        return t('Farmers average values')
      case milkSamplesIDs.dairy:
        return t('Dairy average values')
      case milkSamplesIDs.dailyValues:
        return t('Daily samples')
      case milkSamplesIDs.pseudom:
        return t('Pseudom.')
      default:
        return ''
    }
  }

  function getBackUrl() {
    switch (sampleType) {
      case milkSamplesIDs.allIndividual:
        return RouteTypes.MILK_MONEY_SAMPLES_INDIVIDUAL.replace(':id', sampleType)
      case milkSamplesIDs.farmer:
        return RouteTypes.MILK_MONEY_SAMPLES_FARMER.replace(':id', sampleType)
      case milkSamplesIDs.dairy:
        return RouteTypes.MILK_MONEY_SAMPLES_DAIRY.replace(':id', sampleType)
      case milkSamplesIDs.dailyValues:
        return RouteTypes.MILK_MONEY_SAMPLES_DAIRY.replace(':id', sampleType)
      case milkSamplesIDs.pseudom:
        return RouteTypes.MILK_MONEY_SAMPLES_PSEUDOM.replace(':id', sampleType)
      default:
        return ''
    }
  }

  function getLabelColumns() {
    switch (sampleType) {
      case milkSamplesIDs.allIndividual:
        return showDataLabelsAndKeys
      case milkSamplesIDs.farmer:
        return farmerColumns
      case milkSamplesIDs.dairy:
        return showDataLabelsAndKeys.filter(column => !dairyRemoveKeys.find(key => column.key === key))
      case milkSamplesIDs.dailyValues:
        return showDataLabelsAndKeys.filter(column => column.key !== 'inhibitorLabel')
      case milkSamplesIDs.pseudom:
        return pseudomColumns
      default:
        return []
    }
  }

  async function handleDownloadFile(name) {
    setIsBusy(true)
    try {
      await DownloadService.downloadFile(`milk-money/milk-samples/${sampleType}/${id}/get-file`, name, null, true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderCellValue(value, row) {
    switch (row.type) {
      case 'date':
        return value ? dayjs(value).format(row.format) : ''
      case 'icon':
        return <Icon name={value ? 'check circle outline' : 'close'} color={value ? 'green' : 'red'} />
      case 'number':
        return isNaN(value) ? '' : row.format === 'percent' ? formatNumber(value) : formatNumberWithoutFractions(value)
      case 'file':
        return <div style={{ color: '#2185d0', cursor: 'pointer' }} onClick={() => handleDownloadFile(value)}>{value}</div>
      case 'deductionType':
        return typeof (value) === 'number' ? value ? t('No') : t('Yes') : t('Not defined')
      case 'no-unit-int-number':
        return value ? formatNumber(value, 0) : ''
      default:
        return value || ''
    }
  }

  const labelColumns = getLabelColumns()
  const showDateKey = sampleType === milkSamplesIDs.farmer ? 'billingMonth' : 'date'

  return <Layout
    title={t('Milk samples - {{type}} - {{date}}', { type: getTitle(), date: showData ? dayjs(showData[showDateKey]).format('MMM YYYY') : '' })}
    isBusy={isBusy}
    segmented
    close={getBackUrl()}
  >
    {showData && <Table celled>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('Description label')}</Table.HeaderCell>
          <Table.HeaderCell>{t('Value')}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {labelColumns.map(showDataLabelAndKey => {
          return <Table.Row key={showDataLabelAndKey.key}>
            <Table.Cell>{showDataLabelAndKey.label}</Table.Cell>
            <Table.Cell>{renderCellValue(showData[showDataLabelAndKey.key], showDataLabelAndKey)}</Table.Cell>
          </Table.Row>
        })}
      </Table.Body>
    </Table>
    }
  </Layout>
}
export default ShowSamples